import { Dispatch, SetStateAction, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import cuid from 'cuid';
import { HTTPError } from 'ky';
import { SnackbarKey, closeSnackbar, enqueueSnackbar } from 'notistack';
import { useStore } from '@store/GlobalStore';
import { ToastType } from '@components/toast';
import { getEngine } from '@lio/rule-engine';
import { quoteRatingStatus } from '@models/quoteRating';
import { HeaderSaveButton } from '@pages/QuoteDetails/components/HeaderSaveButton';
import { IConfirmDialog } from '@pages/QuoteDetails/types';
import { Button } from '@components/button';
import { GroupedQuote } from '@lio/rule-engine/utils/quote-details';
import { QuoteStatus } from '@models/quotes';
import { useCreateQuoteRating } from '@services/quoteRating/useCreateQuoteRating';
import { useQuote, useUpdateQuote } from '@services/quotes';
import { CustomerSubmitButton } from '@pages/QuoteDetails/components/CustomerSubmitButton';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { quoteItemQueryKey } from '@services/quoteItem';
import { ProductOfferingId } from '@models/newQuote';
import { quoteQueryKey } from '@services/quotes/keys';
import { useCurrentQuoteStoreState, useQuoteStore } from '@store/Quote/QuoteStore';
import { jam } from '@jam.dev/sdk';

export type QDHeaderButtonsProps = {
  isReadOnly: boolean;
  quoteId: string;
  activeRating: {
    isActive: boolean;
    id: string | undefined;
  };
  onSetActiveRating: Dispatch<
    SetStateAction<{
      isActive: boolean;
      id: string | undefined;
    }>
  >;
  onStatusRead: () => void;
  onSetConfirmDialog: (dialog: Partial<IConfirmDialog>) => void;
  lastRatingJob: { status: string } | null;
  hasAnyPendingProcessingRatings?: boolean;
};

export const QDHeaderButtons = ({
  activeRating,
  isReadOnly,
  quoteId,
  onSetActiveRating,
  onStatusRead,
  onSetConfirmDialog,
  lastRatingJob,
  hasAnyPendingProcessingRatings,
}: QDHeaderButtonsProps) => {
  const navigate = useNavigate();

  const { structuredQuote, panelInstances } = useQuoteStore(
    (state) => ({ structuredQuote: state.structuredQuote, panelInstances: state.panelInstances }),
    shallow,
  );
  const isLoading = !structuredQuote; // EC TODO

  const { isPending: isUpdateQuoteLoading } = useUpdateQuote();
  const queryClient = useQueryClient();
  const { mutateAsync: rateQuote, isPending: isCreateRatingLoading } = useCreateQuoteRating();
  const isMutatingQuoteItemAttributes = useIsMutating({
    mutationKey: quoteItemQueryKey.updateQuoteItemAttributesMutationKey,
  });
  const userAuth = useStore((state) => state.userAuth, shallow);
  const { isBroker, isInternalUser, isInternalWithoutSales, isLioSales, isCustomerUser } = userAuth;

  const [currentEltKey, setCurrentEltKey] = useState<SnackbarKey>('');
  const quoteStoreContext = useCurrentQuoteStoreState();

  const { quote, isFetching: isQuoteFetching } = useQuote(quoteId);
  const isFormsV2Enabled = quote?.formsV2Enabled === true;
  const quoteValidationErrors: Array<{
    ruleId?: string;
    rulePanelSectionId?: string;
    quoteFormId?: string;
    error: { type: string };
  }> | null = useMemo(() => {
    const ruleEngine = getEngine(structuredQuote as GroupedQuote);
    if (structuredQuote && !isLoading) {
      return ruleEngine?.validateQuote(structuredQuote, { includeQuoteItemRules: true }).filter((ve) => {
        // Skip Forms V2 validation errors when Forms V2 is disabled for this quote (based on quote.formsV2Enabled)
        if (!isFormsV2Enabled && ve.quoteFormId) {
          return false;
        }
        if (ve.ruleId || ve.rulePanelSectionId || ve.error.type === 'ValidationRuleFailed') {
          // If the error is a validation rule failure, it should only block if it belongs to a panel section
          return ve.rulePanelSectionId !== undefined;
        }
        return true;
      });
    }

    // TODO: currently the custom ascii checks will not block rate.
    return null;
  }, [structuredQuote, isLoading, isFormsV2Enabled]);

  const isGoToBindDisabled = useMemo(() => {
    if (isBroker) {
      return quote?.status.id !== QuoteStatus.ReadyToBind.id && quote?.status.id !== QuoteStatus.Underwritten.id;
    }
    if (isInternalWithoutSales) {
      return quote?.status.id !== QuoteStatus.ReadyToBind.id && quote?.status.id !== QuoteStatus.Underwritten.id;
    }
    return isReadOnly;
  }, [isBroker, isInternalWithoutSales, isReadOnly, quote?.status.id]);

  const showSubmitButton =
    !isInternalUser &&
    (quote?.status.name === QuoteStatus.Rated.queryParam || quote?.status.name === QuoteStatus.Submission.queryParam);

  const restrictToCustomerSubmit = isCustomerUser && quote?.offering?.id !== ProductOfferingId.SpecialEvent;

  const showBindButton = useMemo(() => {
    return (
      !restrictToCustomerSubmit &&
      !hasAnyPendingProcessingRatings &&
      !isLioSales &&
      (quote?.status.name === QuoteStatus.Underwritten.queryParam ||
        quote?.status.name === QuoteStatus.ReadyToBind.queryParam)
    );
  }, [restrictToCustomerSubmit, hasAnyPendingProcessingRatings, isLioSales, quote?.status]);

  const showRateButton = useMemo(() => {
    return (
      (!isCustomerUser &&
        isInternalWithoutSales &&
        (quote?.status.name === QuoteStatus.Rated.queryParam ||
          quote?.status.name === QuoteStatus.Submission.queryParam ||
          quote?.status.name === QuoteStatus.Referred.queryParam)) ||
      hasAnyPendingProcessingRatings
    );
  }, [isInternalWithoutSales, hasAnyPendingProcessingRatings, isCustomerUser, quote?.status]);

  const [attributesById] = useQuoteStore((state) => [state.attributesById], shallow);
  const unsavedFormAttributes = useQuoteStore((state) => state.unsavedFormAttributes);

  const hasUnsavedChanges = Object.keys(attributesById).length > 0 || Object.keys(unsavedFormAttributes).length > 0;
  const buttons: React.ReactNode[] = [];

  const submitEnabledNonBroker = !activeRating.isActive && quoteValidationErrors?.length === 0 && !hasUnsavedChanges;

  const submitEnabledBroker =
    submitEnabledNonBroker &&
    quote?.status.id !== QuoteStatus.Referred.id &&
    quote?.status.id !== QuoteStatus.Underwritten.id &&
    quote?.status.id !== QuoteStatus.ReadyToBind.id;

  const submitEnabled = isCustomerUser || isBroker ? submitEnabledBroker : submitEnabledNonBroker;

  const isRateDisabled =
    quoteValidationErrors !== null
      ? quoteValidationErrors?.length > 0 ||
        activeRating.isActive ||
        hasUnsavedChanges ||
        isMutatingQuoteItemAttributes > 0
      : true;

  const unsavedAttributeCount = Object.keys(attributesById).length;
  const hasUnsaved = unsavedAttributeCount > 0;

  useEffect(() => {
    jam.metadata(() => ({
      isRateDisabled,
      hasUnsavedChanges: hasUnsaved,
      unsavedAttributeCount,
    }));
  }, [isRateDisabled, hasUnsaved, unsavedAttributeCount]);

  const runRating = async () => {
    if (currentEltKey) {
      closeSnackbar(currentEltKey);
    }
    if (!quoteId) return;
    onStatusRead();
    enqueueSnackbar('Rating In Progress', {
      preventDuplicate: true,
      variant: ToastType.Info,
    });

    onSetActiveRating((prev) => ({ ...prev, isActive: true }));
    await rateQuote(
      { quoteId },
      {
        onError: (error: Error) => {
          if (error instanceof HTTPError) {
            const ratingErrorToastKey = cuid();
            setCurrentEltKey(ratingErrorToastKey);
            onStatusRead();
          }
        },
      },
    ).then((response) => {
      onSetActiveRating((prev) => ({ ...prev, id: response.id }));
    });
  };

  const exitButton = (
    <Button key='exit' variant='outlined' onClick={() => navigate('/')}>
      Exit
    </Button>
  );

  const submitButton = restrictToCustomerSubmit ? (
    <CustomerSubmitButton quoteId={quoteId} variant='contained' panelInstances={panelInstances} />
  ) : (
    <Button
      key='submit-quote'
      variant='contained'
      disabled={!submitEnabled}
      isLoading={isUpdateQuoteLoading}
      onClick={async () => {
        await runRating();

        if (lastRatingJob?.status === quoteRatingStatus.Succeeded) {
          if (isCustomerUser) {
            // clear out quote status to reflect this otherwise user will be kicked back
            await queryClient.invalidateQueries({ queryKey: quoteQueryKey.get(quoteId) });
          }
          navigate(`/quote/${quoteId}/review`);
        }
      }}
    >
      Submit Quote
    </Button>
  );
  const rateButton = (
    <Button
      key='rate'
      variant='outlined'
      onClick={async () => runRating()}
      isLoading={isCreateRatingLoading || isQuoteFetching || activeRating.isActive}
      disabled={isRateDisabled}
    >
      Rate
    </Button>
  );

  const bindButtonClick = async () => {
    const { attributesById, clearAttributeValues } = quoteStoreContext;
    const hasUnsavedChanges = Object.keys(attributesById).length > 0;
    if (!hasUnsavedChanges) {
      clearAttributeValues(Object.keys(attributesById));
      // set variables for bind quote
      navigate(`/quote/${quoteId}/review`);
      onSetConfirmDialog({
        show: false,
      });
    } else {
      onSetConfirmDialog({
        show: true,
        description: 'There are unsaved changes. Discard changes and proceed?',
        confirmText: 'Yes, proceed to bind',
        cancelText: 'No, return to quote',
        navigatePath: `/quote/${quoteId}/review`,
      });
    }
  };

  const bindButton = (
    <Button key='review' variant='contained' disabled={isGoToBindDisabled} onClick={bindButtonClick}>
      Review & Finalize
    </Button>
  );

  buttons.push(<HeaderSaveButton key='save-button' />);

  if ((isLioSales || isBroker) && !isCustomerUser) {
    buttons.push(exitButton);
  }
  if (showRateButton) {
    buttons.push(rateButton);
  }
  if (showSubmitButton) {
    buttons.push(submitButton);
  }

  if (showBindButton) {
    buttons.push(bindButton);
  }

  return buttons;
};
